import React, { useState, useEffect, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { createPreferencesAdapter } from '@tldr/core/adapters/preferences';
import i18n, { SUPPORTED_LANGUAGES, LANGUAGE_PREFERENCE_KEY } from '../i18n/config';
import { saveLanguagePreference } from '../services/tokenBridge';
import { LanguageContext, type LanguageContextType } from './LanguageContext';

// Issue #455 D1 — Capacitor Preferences 직접 import 대신 어댑터 경유.
const preferences = createPreferencesAdapter();

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<string>(i18n.language || 'en');

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguageState(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // 앱 시작 시 현재 언어를 App Groups에 동기화 (Share Extension용)
  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      saveLanguagePreference(i18n.language).catch((error) => {
        console.warn('[LanguageContext] Failed to sync language to App Groups:', error);
      });
    }
  }, []);

  const setLanguage = async (lang: string) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      console.warn(`Language '${lang}' is not supported. Fallback to 'en'.`);
      lang = 'en';
    }

    // Preferences 어댑터에 저장 (AI 요약 언어로 사용)
    // 어댑터 web 변형은 localStorage 폴백, native는 @capacitor/preferences 위임.
    await preferences.set(LANGUAGE_PREFERENCE_KEY, lang);

    // iOS App Groups에도 저장 (Share Extension 공유용)
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      try {
        await saveLanguagePreference(lang);
      } catch (error) {
        console.warn('[LanguageContext] Failed to save language to App Groups:', error);
      }
    }

    // i18n 언어 변경 (UI 번역이 없는 언어는 자동으로 'en'으로 fallback)
    await i18n.changeLanguage(lang);
    setLanguageState(lang);
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
